import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Alert,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
} from '@mui/material';
import {
  EmojiEvents as RankingIcon,
  Person as PersonIcon,
  SportsHandball as BadmintonIcon,
  SportsCricket as CricketIcon,
  SportsTennis as TennisIcon,
  SportsSoccer as FootballIcon,
  Sports as AthleticsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ArrowBack as ArrowBackIcon,
  Menu as MenuIcon,
  ExitToApp as LogoutIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminService, { Player } from '@services/adminService';
import RankingEvaluationWithTabs from '@components/performance/RankingEvaluationWithTabs';
const DRAWER_WIDTH = 280;

const CoachPlayerRankingPage: React.FC = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [myStudents, setMyStudents] = useState<Player[]>([]);
  const [filteredPlayers, setFilteredPlayers] = useState<Player[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<string>('');
  const [coachId, setCoachId] = useState<string>('');
  const [expandedSports, setExpandedSports] = useState<string[]>(['Badminton']);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Get player ID, sport, and category from URL
  const urlParams = new URLSearchParams(location.search);
  const playerIdFromUrl = urlParams.get('player');
  const pathParts = location.pathname.split('/').filter(Boolean);
  const rankingIndex = pathParts.indexOf('player-ranking');
  const sportFromPath = rankingIndex >= 0 && pathParts.length > rankingIndex + 1 ? pathParts[rankingIndex + 1] : '';
  const categoryFromPath = rankingIndex >= 0 && pathParts.length > rankingIndex + 2 ? pathParts[rankingIndex + 2] : '';

  // Available sports
  const availableSports = ['Badminton', 'Cricket', 'Tennis', 'Football', 'Athletics'];
  const comingSoonSports = ['Cricket', 'Tennis', 'Football', 'Athletics'];

  useEffect(() => {
    if (user) {
      loadMyStudents();
    }
  }, [user]);

  // Don't filter players by sport - show all players
  // Players can select any sport and evaluate, regardless of their primary/secondary sport
  useEffect(() => {
    // Always show all players - don't filter by sport
    setFilteredPlayers(myStudents);
  }, [myStudents]);

  useEffect(() => {
    // Only set selectedPlayer after filteredPlayers is populated
    if (playerIdFromUrl) {
      // Check if the player from URL exists in filteredPlayers
      const playerExists = filteredPlayers.find(p => p.id === playerIdFromUrl);
      if (filteredPlayers.length > 0) {
        if (playerExists) {
          // Player exists in filtered list, set it
          if (selectedPlayer !== playerIdFromUrl) {
            setSelectedPlayer(playerIdFromUrl);
          }
        } else {
          // Player from URL doesn't exist in filtered list
          // Check if player exists in myStudents (might be filtered out by sport)
          const playerInAllStudents = myStudents.find(p => p.id === playerIdFromUrl);
          if (playerInAllStudents) {
            // Player exists but doesn't match sport filter - clear selection or select first available
            if (filteredPlayers.length > 0) {
              setSelectedPlayer(filteredPlayers[0].id);
              // Update URL to reflect the new selection
              if (sportFromPath && categoryFromPath) {
                navigate(`/coach/player-ranking/${sportFromPath}/${categoryFromPath}?player=${filteredPlayers[0].id}`, { replace: true });
              }
            } else {
              setSelectedPlayer('');
            }
          } else {
            // Player doesn't exist at all - clear selection
            setSelectedPlayer('');
          }
        }
      }
      // If filteredPlayers is empty, wait for it to be populated
      return; // Don't proceed to auto-select if we have a URL player
    }
    
    // Priority 2: Auto-select first filtered player if none selected and we have filtered players
    // BUT only if we're on a page that requires a player (sport and category selected)
    if (sportFromPath && categoryFromPath && filteredPlayers.length > 0 && !selectedPlayer) {
      setSelectedPlayer(filteredPlayers[0].id);
      navigate(`/coach/player-ranking/${sportFromPath}/${categoryFromPath}?player=${filteredPlayers[0].id}`);
    } else if (filteredPlayers.length === 0 && selectedPlayer && myStudents.length > 0) {
      // If filteredPlayers is empty but we have students, it means filtering is in progress
      // Don't clear selectedPlayer yet - wait for filtering to complete
      return;
    } else if (filteredPlayers.length === 0 && selectedPlayer && myStudents.length === 0) {
      // No students at all, clear selection
      setSelectedPlayer('');
    }
  }, [playerIdFromUrl, filteredPlayers, selectedPlayer, myStudents, sportFromPath, categoryFromPath, navigate]);

  const loadMyStudents = async () => {
    let coachId = (user as any)?.coachId;
    
    // If coachId is not in user object, try to fetch it from the API
    if (!coachId && user?.id) {
      try {
        const response = await fetch(`/api/coaches/me?userId=${user.id}`);
        const data = await response.json();
        if (data.success && data.data) {
          coachId = data.data.id;
          // Update user object with coachId and other data
          const updatedUser = { 
            ...user, 
            coachId: data.data.id, 
            coachIdCode: data.data.coachIdCode,
            firstName: data.data.user.firstName,
            lastName: data.data.user.lastName,
          };
          const { setUser } = useAuthStore.getState();
          setUser(updatedUser);
        }
      } catch (err) {
        console.error('Error fetching coach data:', err);
      }
    }
    
    if (!coachId) {
      setError('Coach ID not found. Please contact support if this issue persists.');
      setLoading(false);
      return;
    }
    setCoachId(String(coachId));

    setLoading(true);
    try {
      const response = await AdminService.getPlayersForCoach(coachId);
      const players = response.data || [];
      setMyStudents(players);
      
      // Don't set selectedPlayer here - let the useEffect handle it after filteredPlayers is set
      // This prevents the race condition where selectedPlayer is set but filteredPlayers is empty
    } catch (err: any) {
      console.error('Error loading students:', err);
      setError('Failed to load students');
      setMyStudents([]);
      setFilteredPlayers([]);
      setSelectedPlayer('');
    } finally {
      setLoading(false);
    }
  };

  const getSportIcon = (sport: string) => {
    switch (sport.toLowerCase()) {
      case 'badminton':
        return <BadmintonIcon />;
      case 'tennis':
        return <TennisIcon />;
      case 'football':
        return <FootballIcon />;
      case 'cricket':
        return <CricketIcon />;
      case 'athletics':
        return <AthleticsIcon />;
      default:
        return <BadmintonIcon />;
    }
  };

  const handleToggleSport = (sport: string) => {
    if (comingSoonSports.includes(sport)) {
      return;
    }
    setExpandedSports(prev =>
      prev.includes(sport)
        ? prev.filter(s => s !== sport)
        : [...prev, sport]
    );
  };

  const handleCategoryClick = (sport: string, categoryRoute: string) => {
    if (comingSoonSports.includes(sport)) {
      return;
    }
    // Navigate to the category page - player can be selected after
    if (selectedPlayer) {
      navigate(`/coach/player-ranking/${sport}/${categoryRoute}?player=${selectedPlayer}`);
    } else {
      navigate(`/coach/player-ranking/${sport}/${categoryRoute}`);
    }
  };

  const handlePlayerChange = (playerId: string) => {
    setSelectedPlayer(playerId);
    if (sportFromPath && categoryFromPath) {
      navigate(`/coach/player-ranking/${sportFromPath}/${categoryFromPath}?player=${playerId}`);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleBack = () => {
    navigate('/coach/dashboard');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Ranking Evaluation Categories (for coach login, exclude Self Evaluation)
  const rankingCategories = [
    {
      id: 'coach-academy',
      label: 'Coach/Academy Evaluation',
      icon: <RankingIcon />,
      color: '#2e7d32',
      route: 'coach-academy',
    },
    {
      id: 'practical-test',
      label: 'Board Exam',
      icon: <RankingIcon />,
      color: '#ed6c02',
      route: 'practical-test',
    },
  ];

  const drawer = (
    <Box sx={{ bgcolor: '#e8f4f8', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Player Ranking
        </Typography>
      </Toolbar>
      <List>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={handleBack}
            sx={{
              color: '#1976d2',
              '&:hover': {
                bgcolor: '#e3f2fd',
              }
            }}
          >
            <ListItemIcon sx={{ color: '#1976d2' }}>
              <ArrowBackIcon />
            </ListItemIcon>
            <ListItemText primary="Back to Dashboard" />
          </ListItemButton>
        </ListItem>
      </List>

      <List>
        {/* Sports as Parent Items */}
        {availableSports.map((sport) => {
          const isComingSoon = comingSoonSports.includes(sport);
          
          return (
            <React.Fragment key={sport}>
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => !isComingSoon && handleToggleSport(sport)}
                  disabled={isComingSoon}
                  sx={{
                    opacity: isComingSoon ? 0.5 : 1,
                    cursor: isComingSoon ? 'default' : 'pointer',
                    '&:hover': {
                      bgcolor: isComingSoon ? 'transparent' : 'action.hover',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.5,
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: isComingSoon ? '#9e9e9e' : '#1976d2' }}>
                    {getSportIcon(sport)}
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          sx={{ 
                            fontSize: '0.9rem', 
                            fontWeight: 700,
                            color: isComingSoon ? '#9e9e9e' : '#1976d2'
                          }}
                        >
                          {sport}
                        </Typography>
                        {isComingSoon && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#9e9e9e',
                              fontStyle: 'italic',
                              fontSize: '0.75rem',
                            }}
                          >
                            (Coming Soon)
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  {!isComingSoon && (
                    expandedSports.includes(sport) ? (
                      <ExpandLessIcon sx={{ color: '#1976d2' }} />
                    ) : (
                      <ExpandMoreIcon sx={{ color: '#1976d2' }} />
                    )
                  )}
                </ListItemButton>
              </ListItem>
              {!isComingSoon && (
                <Collapse in={expandedSports.includes(sport)} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {rankingCategories.map((category) => {
                      const isSelected = sportFromPath === sport && categoryFromPath === category.route;
                      return (
                        <ListItemButton
                          key={category.id}
                          sx={{ pl: 4 }}
                          selected={isSelected}
                          onClick={() => handleCategoryClick(sport, category.route)}
                        >
                          <ListItemIcon sx={{ minWidth: 32, color: isSelected ? category.color : '#666' }}>
                            {category.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={category.label}
                            primaryTypographyProps={{ 
                              fontSize: '0.85rem', 
                              fontWeight: isSelected ? 700 : 600,
                              color: isSelected ? category.color : 'inherit'
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>
      <Box sx={{ mt: 'auto', borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={handleLogout}
            sx={{
              color: '#d32f2f',
              '&:hover': {
                bgcolor: '#ffebee',
              },
            }}
          >
            <ListItemIcon sx={{ color: '#d32f2f' }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Logout"
              primaryTypographyProps={{ 
                fontSize: '0.9rem', 
                fontWeight: 600,
                color: '#d32f2f'
              }}
            />
          </ListItemButton>
        </ListItem>
      </Box>
    </Box>
  );

  const selectedPlayerData = filteredPlayers.find(p => p.id === selectedPlayer);

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      );
    }

    // If no sport or category selected, show default message (like My Ranking page)
    if (!sportFromPath || !categoryFromPath) {
      return (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            Welcome to Player Rankings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Select a sport and evaluation type from the sidebar to get started.
          </Typography>
        </Box>
      );
    }

    // If sport and category are selected but no player, show message
    if (!selectedPlayer) {
      return (
        <Alert severity="info">
          Please select a player from the dropdown to view their ranking data.
        </Alert>
      );
    }

    if (!selectedPlayerData) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      );
    }

    // Map category route to categoryId (Self Evaluation removed for coach login)
    const categoryMap: Record<string, string> = {
      'coach-academy': 'coach-academy',
      'practical-test': 'practical-test',
    };

    const categoryId = categoryMap[categoryFromPath] || 'coach-academy';
    const categoryLabel = rankingCategories.find(c => c.id === categoryId)?.label || 'Coach/Academy Evaluation';

    return (
      <RankingEvaluationWithTabs
        player={selectedPlayerData}
        categoryId={categoryId}
        categoryLabel={categoryLabel}
        coachId={coachId}
        defaultSport={sportFromPath}
      />
    );
  };

  // Early return if user is not available
  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Alert severity="error">Please log in to access this page.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: DRAWER_WIDTH,
              bgcolor: '#e8f4f8'
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: DRAWER_WIDTH,
              bgcolor: '#e8f4f8'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          mt: 0
        }}
      >
        <Container maxWidth="xl">
          {/* Player Selection at Top - Only show when sport and category are selected */}
          {sportFromPath && categoryFromPath && (
            <Box sx={{ mb: 3 }}>
              <FormControl sx={{ minWidth: 300 }} size="small">
                <InputLabel>Select Player</InputLabel>
                <Select
                  value={filteredPlayers.find(p => p.id === selectedPlayer) ? selectedPlayer : ''}
                  label="Select Player"
                  onChange={(e) => handlePlayerChange(e.target.value)}
                  disabled={filteredPlayers.length === 0}
                >
                  {filteredPlayers.map((player) => (
                    <MenuItem key={player.id} value={player.id}>
                      {player.playerIdCode || player.id} - {player.user?.firstName || ''} {player.user?.lastName || ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {filteredPlayers.length === 0 && !comingSoonSports.includes(sportFromPath) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No players found for {sportFromPath}
                </Typography>
              )}
            </Box>
          )}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {renderContent()}
        </Container>
      </Box>
    </Box>
  );
};

export default CoachPlayerRankingPage;

